import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, mediaUrl } from "../../lib/api";
import { useToast } from "../../lib/toast";
import MediaUploader from "../../components/MediaUploader";

export default function AdminHomepageHero() {
  const toast = useToast();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [titles, setTitles] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/homepage/hero-videos");
      setVideos(response.data || []);
      setTitles(Object.fromEntries((response.data || []).map((video) => [video.id, video.title || ""])));
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const addVideo = async (upload) => {
    if (!upload?.id) {
      toast("The video upload completed without a media ID.", "error");
      return;
    }
    try {
      await api.post("/admin/homepage/hero-videos", {
        media_id: upload.id,
        title: upload.title || "Homepage video",
      });
      toast("Homepage video added.", "success");
      await load();
    } catch (error) {
      try {
        await api.delete(`/media/${upload.id}`);
      } catch (cleanupError) {
        toast(`${formatApiError(error)} Uploaded media cleanup failed: ${formatApiError(cleanupError)}`, "error");
        return;
      }
      toast(formatApiError(error), "error");
    }
  };

  const updateVideo = async (video, changes) => {
    setBusyId(video.id);
    try {
      await api.patch(`/admin/homepage/hero-videos/${video.id}`, changes);
      toast("Homepage video updated.", "success");
      await load();
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setBusyId("");
    }
  };

  const moveVideo = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= videos.length) return;
    const reordered = [...videos];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setBusyId(reordered[index].id);
    try {
      await Promise.all(reordered.map((video, order) => (
        api.patch(`/admin/homepage/hero-videos/${video.id}`, { order })
      )));
      await load();
    } catch (error) {
      toast(formatApiError(error), "error");
      await load();
    } finally {
      setBusyId("");
    }
  };

  const removeVideo = async (video) => {
    if (!window.confirm(`Remove "${video.title}" from the homepage? This also deletes the uploaded video.`)) return;
    setBusyId(video.id);
    try {
      await api.delete(`/admin/homepage/hero-videos/${video.id}`);
      toast("Homepage video removed.", "success");
      await load();
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="pad-24" data-testid="admin-homepage-hero">
      <h2 className="font-serif fs-24 fw-700 mb-8">Homepage Hero Videos</h2>
      <p className="text-muted fs-13 mb-16">
        Upload short MP4 or WebM event clips. The first enabled clip in this list is used as the homepage hero video.
        Reorder clips to choose which one appears; disable a clip to skip it. Use compressed files for faster page loads.
      </p>

      <div className="card card-pad mb-16">
        <h3 className="fs-16 fw-700 mb-8">Upload event clips</h3>
        <MediaUploader
          type="video"
          accept="video/mp4,video/webm,video/quicktime"
          maxFiles={5}
          onComplete={addVideo}
        />
      </div>

      <div className="card card-pad">
        <div className="flex-between mb-12">
          <h3 className="fs-16 fw-700">Current clips</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
        {loading ? (
          <p className="text-muted fs-13">Loading homepage videos…</p>
        ) : videos.length === 0 ? (
          <p className="text-muted fs-13">No uploaded hero videos. The homepage will show its default visual until clips are added.</p>
        ) : (
          <div style={{ display: "grid", gap: "var(--space-3)" }}>
            {videos.map((video, index) => (
              <div
                key={video.id}
                className="card"
                data-testid={`homepage-hero-video-${video.id}`}
                style={{ display: "grid", gridTemplateColumns: "minmax(140px, 240px) minmax(0, 1fr)", gap: "var(--space-3)", padding: "var(--space-3)", alignItems: "center" }}
              >
                <video
                  src={`${mediaUrl(video.id)}/file`}
                  poster={`${mediaUrl(video.id)}/thumb`}
                  muted
                  playsInline
                  controls
                  preload="metadata"
                  style={{ width: "100%", maxHeight: 140, borderRadius: 8, background: "#17121f" }}
                />
                <div style={{ minWidth: 0 }}>
                  <label className="field-label" htmlFor={`hero-video-title-${video.id}`}>Clip title</label>
                  <input
                    id={`hero-video-title-${video.id}`}
                    className="field-input mb-8"
                    value={titles[video.id] ?? video.title ?? ""}
                    onChange={(event) => setTitles((state) => ({ ...state, [video.id]: event.target.value }))}
                    maxLength={200}
                    data-testid={`homepage-hero-video-title-${video.id}`}
                  />
                  <div className="flex-row gap-8" style={{ flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn btn-gold btn-sm"
                      onClick={() => updateVideo(video, { title: titles[video.id] ?? video.title })}
                      disabled={busyId === video.id}
                    >
                      Save title
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => updateVideo(video, { enabled: video.enabled === false })}
                      disabled={busyId === video.id}
                    >
                      {video.enabled === false ? "Show on homepage" : "Hide from homepage"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => moveVideo(index, -1)}
                      disabled={index === 0 || !!busyId}
                      aria-label={`Move ${video.title} up`}
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => moveVideo(index, 1)}
                      disabled={index === videos.length - 1 || !!busyId}
                      aria-label={`Move ${video.title} down`}
                    >
                      Move down
                    </button>
                    <button
                      type="button"
                      className="btn btn-red btn-sm"
                      onClick={() => removeVideo(video)}
                      disabled={busyId === video.id}
                    >
                      Delete
                    </button>
                  </div>
                  <div className="text-muted fs-12 mt-8">
                    {video.enabled === false ? "Hidden" : "Visible"} · {video.mime || "video"} · {Math.ceil((video.size || 0) / (1024 * 1024))} MB
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
